import * as React from 'react';
import { SetOptionalState } from '../models/react-model';
import { CycleComboContainer } from '../../entry/containers/cycle-combo-container';
import { useCycleComboService } from '../services/cycle-service';
import { CycleComboModel, isCycleActive } from '../models/cycle-model';

interface CycleContextValue {
  selectedCycleId?: number;
  selectedCycle?: CycleComboModel;
  isSelectedCycleActive: boolean;
  setSelectedCycleId: SetOptionalState<number>;
  renderCycleCombo: () => JSX.Element;
}

const CycleContext = React.createContext<CycleContextValue>({
  selectedCycleId: undefined,
  selectedCycle: undefined,
  isSelectedCycleActive: false,
  setSelectedCycleId: () => undefined,
  renderCycleCombo: () => <React.Fragment />,
});

export const CycleProvider: React.FC = ({ children }) => {
  const { fetchCycleCombo, fetchCycleComboLoading, fetchCycleComboError } =
    useCycleComboService();

  const [selectedCycleId, setSelectedCycleId] = React.useState<number>();
  const [cycles, setCycles] = React.useState<CycleComboModel[]>();

  const selectedCycle = React.useMemo(
    () => cycles?.find((cycle) => cycle.id === selectedCycleId),
    [cycles, selectedCycleId],
  );

  const loadCycles = React.useCallback(
    () =>
      fetchCycleCombo().then((data) => {
        setCycles(data);
        return data;
      }),
    [fetchCycleCombo],
  );

  const renderCycleCombo = React.useCallback(
    () => (
      <CycleComboContainer
        data={cycles}
        fetchCycleCombo={loadCycles}
        fetchCycleComboError={fetchCycleComboError}
        isLoading={fetchCycleComboLoading}
        onChange={(selectedId) => {
          if (!selectedId) return;
          setSelectedCycleId(selectedId);
        }}
      />
    ),
    [cycles, fetchCycleComboLoading, fetchCycleComboError, loadCycles],
  );

  React.useEffect(() => {
    loadCycles().catch(() => undefined);
  }, [loadCycles]);

  return (
    <CycleContext.Provider
      value={{
        selectedCycleId,
        selectedCycle,
        isSelectedCycleActive: isCycleActive(selectedCycle),
        setSelectedCycleId,
        renderCycleCombo,
      }}
    >
      {children}
    </CycleContext.Provider>
  );
};

export const useCycleContext = () => React.useContext(CycleContext);
